import { Dialog, Typography } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import { Box, SystemStyleObject } from '@mui/system';
import React from 'react';

import { LoadingButton } from '@/components/molecules/LoadingButton';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { OneTimePasswordInput } from '@/external/components/molecules/OneTimePasswordInput';
import { useTranslator } from '@/hooks/useTranslator';

interface Disable2FAOTPModalProps {
  readonly open: boolean;
  readonly loading?: boolean;
  readonly error?: string;

  onConfirm(otp: string): void;
  onAbort(): void;
}

export const Disable2FAOTPModal: React.FC<Disable2FAOTPModalProps> = (
  props: Disable2FAOTPModalProps,
): React.ReactElement => {
  const { open, onConfirm, onAbort, loading = false, error } = props;
  const [code, setCode] = React.useState<readonly string[]>(['', '', '', '', '', '']);

  const tr = useTranslator();

  const handleSubmit = React.useCallback(
    (event: React.FormEvent): void => {
      event.preventDefault();
      onConfirm?.(code.join(''));
    },
    [code, onConfirm],
  );

  const handleClose = React.useCallback((): void => {
    onAbort();
  }, [onAbort]);

  const onCodeChange = React.useCallback((value: readonly string[]): void => {
    setCode(value);
  }, []);

  // Reset OTP when modal closes
  React.useEffect((): void => {
    if (!open) {
      setCode(['', '', '', '', '', '']);
    }
  }, [open]);

  return (
    <Dialog open={open} slotProps={slotProps}>
      <ApplicationModal
        title={tr('PROVIDE_THE_SIX_DIGIT_CODE')}
        width={552}
        variant="prominent"
        onClose={handleClose}>
        <Box component="form" sx={styles.form} onSubmit={handleSubmit}>
          <OneTimePasswordInput
            value={code}
            error={Boolean(error)}
            digits={6}
            size="small"
            onChange={onCodeChange}
          />

          <FormHelperText error={Boolean(error)}>{error}</FormHelperText>

          <Box width="100%">
            <LoadingButton type="submit" variant="contained" fullWidth={true} loading={loading}>
              {tr('SUBMIT')}
            </LoadingButton>
            <Box component="a" sx={styles.customButton} onClick={handleClose}>
              <Typography variant="body2" color="text.primary">
                {tr('CANCEL')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </ApplicationModal>
    </Dialog>
  );
};

const styles: Record<string, SystemStyleObject> = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  customButton: {
    display: 'block',
    textAlign: 'center',
    mt: 2,
    cursor: 'pointer',
  },
};

const slotProps = {
  paper: {
    sx: {
      minWidth: 0,
    },
  },
};
